import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Check,
  Compass,
  Copy,
  Crown,
  Flag,
  Home,
  LockKeyhole,
  LogOut,
  Mountain,
  Plus,
  RefreshCw,
  Ticket as TicketIcon,
  TrainFront,
  Trophy,
  Users,
  Waves,
  X,
} from "lucide-react";
import type { User, RoomView } from "../server/types";
import type {
  CardColor,
  GameAction,
  GameView,
  Route,
  Ticket,
} from "../game/types";
import { COLORS } from "../game/types";
import { ROUTES, ROUTE_POINTS } from "../game/data";
import { Board, cityName } from "./Board";
import { Destination, Modal, Rules, TrainCard } from "./ui";
import { api, recentRooms, rememberRoom, RequestError } from "./api";

type Session = {
  user: User | null;
  googleEnabled: boolean;
  devLoginEnabled: boolean;
};
const allColors: CardColor[] = [...COLORS, "locomotive"];
const getRoomCode = () =>
  /^\/room\/([A-Z2-9]{10})\/?$/i.exec(location.pathname)?.[1]?.toUpperCase() ??
  null;

export function App() {
  const [session, setSession] = useState<Session | null>(null),
    [code, setCode] = useState(getRoomCode),
    [room, setRoom] = useState<RoomView | null>(null);
  const [error, setError] = useState(
      new URLSearchParams(location.search).has("authError")
        ? "Google sign-in did not finish. Please try again."
        : "",
    ),
    [busy, setBusy] = useState(false),
    [rules, setRules] = useState(false),
    [connected, setConnected] = useState(true),
    [copied, setCopied] = useState(false),
    [joinCode, setJoinCode] = useState(""),
    [needsJoin, setNeedsJoin] = useState(false);
  const revision = useRef(-1),
    polling = useRef(false),
    activeCode = useRef(code);
  const navigate = useCallback((next: string | null) => {
    history.pushState({}, "", next ? `/room/${next}` : "/");
    activeCode.current = next;
    revision.current = -1;
    setRoom(null);
    setNeedsJoin(false);
    setCode(next);
    setError("");
  }, []);
  useEffect(() => {
    const fn = () => {
      activeCode.current = getRoomCode();
      revision.current = -1;
      setRoom(null);
      setNeedsJoin(false);
      setCode(activeCode.current);
    };
    window.addEventListener("popstate", fn);
    api<Session>("/api/session")
      .then(setSession)
      .catch(() => setError("Unable to connect. Please refresh to try again."));
    return () => window.removeEventListener("popstate", fn);
  }, []);
  const acceptRoom = useCallback((next: RoomView) => {
    if (next.id !== activeCode.current) return;
    if (next.revision >= revision.current) {
      revision.current = next.revision;
      setRoom(next);
      setNeedsJoin(false);
      rememberRoom(next.id);
    }
    setConnected(true);
  }, []);
  useEffect(() => {
    if (!code || !session?.user) return;
    let active = true;
    const load = async () => {
      if (polling.current) return;
      polling.current = true;
      try {
        const next = await api<RoomView>(`/api/rooms/${code}`);
        if (active) acceptRoom(next);
      } catch (e) {
        if (!active) return;
        if (e instanceof RequestError && e.status === 403) {
          setNeedsJoin(true);
        } else if (e instanceof RequestError && e.status === 401) {
          setSession((s) => (s ? { ...s, user: null } : s));
        } else if (e instanceof RequestError && e.status === 404) {
          setError(e.message);
          setNeedsJoin(true);
        } else setConnected(false);
      } finally {
        polling.current = false;
      }
    };
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 2000);
    const focus = () => void load();
    window.addEventListener("focus", focus);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", focus);
    };
  }, [code, session?.user?.id, acceptRoom]);
  const run = async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      return await work();
    } catch (e) {
      if (e instanceof RequestError) {
        if (e.room) acceptRoom(e.room);
        setError(e.message);
      } else
        setError(
          e instanceof Error
            ? e.message
            : "Something went wrong. Please try again.",
        );
    } finally {
      setBusy(false);
    }
  };
  const create = () =>
    run(async () => {
      const next = await api<RoomView>("/api/rooms", {});
      navigate(next.id);
      acceptRoom(next);
    });
  const join = (id: string) =>
    run(async () => {
      const next = await api<RoomView>(`/api/rooms/${id}/join`, {});
      if (code !== id) navigate(id);
      acceptRoom(next);
    });
  const action = (value: GameAction) =>
    run(async () => {
      if (!room) return false;
      const next = await api<RoomView>(`/api/rooms/${room.id}/action`, {
        revision: room.revision,
        action: value,
      });
      acceptRoom(next);
      return true;
    });
  const login = () => {
    const target = code ? `/room/${code}` : "/";
    location.href = `/api/auth/login?returnTo=${encodeURIComponent(target)}`;
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${location.origin}/room/${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setError("Copy the room link from your address bar to invite friends.");
    }
  };
  const logout = () =>
    run(async () => {
      await api("/api/auth/logout", {});
      setSession((s) => (s ? { ...s, user: null } : s));
      navigate(null);
    });
  const user = session?.user;
  return (
    <>
      <header className="site-header">
        <button
          className="brand"
          onClick={() => navigate(null)}
          aria-label="Ticket to Ride home"
        >
          <span className="brand-icon">
            <TrainFront size={23} />
          </span>
          <span>
            Ticket to Ride<span className="brand-edition">EUROPE</span>
          </span>
        </button>
        <nav>
          <button className="text-button" onClick={() => setRules(true)}>
            <BookOpen size={17} />
            <span>How to play</span>
          </button>
          {user ? (
            <>
              <span className="account">
                <span className="avatar">
                  {user.name.slice(0, 1).toUpperCase()}
                </span>
                <span>{user.name.split(" ")[0]}</span>
              </span>
              <button
                className="icon-button"
                aria-label="Sign out"
                onClick={logout}
              >
                <LogOut size={17} />
              </button>
            </>
          ) : (
            <a className="labs-link" href="https://labs.arjunkhera.io/">
              An experiment by Labs <ArrowUpRight size={15} />
            </a>
          )}
        </nav>
      </header>
      {error && (
        <div className="error-banner" role="alert">
          <span>{error}</span>
          <button
            className="icon-button"
            aria-label="Dismiss error"
            onClick={() => setError("")}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {!code ? (
        <main className="home-page">
          <section className="hero">
            <div className="hero-copy">
              <div className="eyebrow">
                <span className="tiny-line" /> THE EUROPEAN COLLECTION
              </div>
              <h1>
                A continent.
                <br />A few friends.
                <br />
                <em>Endless routes.</em>
              </h1>
              <p>
                From the misty streets of Edinburgh to the sunlit shores of
                Constantinople. Your next great game night starts here.
              </p>
              <div className="hero-meta">
                <span>
                  <Users size={16} />
                  2–5 players
                </span>
                <span>
                  <Compass size={16} />
                  45–90 minutes
                </span>
                <span>
                  <LockKeyhole size={15} />
                  Private tables
                </span>
              </div>
              {user ? (
                <button
                  className="primary large"
                  onClick={create}
                  disabled={busy}
                >
                  {busy ? "Opening your table…" : "Create a table"}
                  <Plus size={19} />
                </button>
              ) : (
                <button
                  className="primary large google-button"
                  onClick={login}
                  disabled={!session?.googleEnabled}
                >
                  <GoogleMark />
                  {!session ? "Connecting…" : "Continue with Google"}
                  <ArrowRight size={18} />
                </button>
              )}
              <div className="hero-caption">
                {user
                  ? "Make a little room for a grand adventure."
                  : "Sign in, invite your friends, and get on board."}
              </div>
              {session && !session.googleEnabled && !user && (
                <p className="setup-note">
                  Google sign-in is being configured. You can explore the map
                  and rules in the meantime.
                </p>
              )}
              {session?.devLoginEnabled && !user && (
                <DevLogin
                  onLogin={(u) =>
                    setSession((s) => (s ? { ...s, user: u } : s))
                  }
                  onError={setError}
                />
              )}
            </div>
            <div className="hero-art">
              <div className="map-postcard">
                <div className="postcard-header">
                  <span>LES CHEMINS DE FER EUROPÉENS</span>
                  <span>Nº 001</span>
                </div>
                <Board decorative />
                <div className="postcard-footer">
                  <span>EUROPE</span>
                  <span>THE JOURNEY IS THE GAME.</span>
                  <TrainFront size={22} />
                </div>
              </div>
              <div className="travel-stamp">
                <span>ALL ABOARD</span>
                <TrainFront size={25} />
                <span>EST. 1901</span>
              </div>
            </div>
          </section>
          <section className="departure-section">
            <div className="section-caption">
              <span>YOUR DEPARTURE LOUNGE</span>
              <span>EVERY JOURNEY STARTS WITH A TICKET</span>
            </div>
            <div className="departure-grid">
              <div className="join-panel">
                <span className="outline-icon">
                  <TicketIcon size={23} />
                </span>
                <div>
                  <h2>Already have a ticket?</h2>
                  <p>Enter the table code your friend shared.</p>
                  <form
                    className="join-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const id = joinCode
                        .replace(/[^A-Z2-9]/gi, "")
                        .toUpperCase();
                      if (id.length !== 10) {
                        setError("Table codes have 10 letters and numbers.");
                        return;
                      }
                      if (user) void join(id);
                      else navigate(id);
                    }}
                  >
                    <input
                      aria-label="Table code"
                      placeholder="YOUR TABLE CODE"
                      value={joinCode}
                      maxLength={12}
                      onChange={(e) =>
                        setJoinCode(e.target.value.toUpperCase())
                      }
                    />
                    <button className="secondary" type="submit" disabled={busy}>
                      Join table <ArrowRight size={17} />
                    </button>
                  </form>
                </div>
              </div>
              <div className="journey-panel">
                <span className="outline-icon">
                  <Mountain size={24} />
                </span>
                <div>
                  <h2>Europe, in all its adventure.</h2>
                  <p>
                    Brave the tunnels. Cross the seas. Connect your cities.
                    <br />
                    The complete base game, with a seat for everyone.
                  </p>
                  <button
                    className="text-button underlined"
                    onClick={() => setRules(true)}
                  >
                    A quick guide to the game <ArrowUpRight size={16} />
                  </button>
                </div>
              </div>
            </div>
            {user && recentRooms().length > 0 && (
              <div className="recent-rooms">
                <span>RECENT TABLES</span>
                {recentRooms().map((id) => (
                  <button key={id} onClick={() => navigate(id)}>
                    {id}
                    <ArrowUpRight size={13} />
                  </button>
                ))}
              </div>
            )}
          </section>
        </main>
      ) : !user ? (
        <main className="center-page">
          <div className="boarding-pass">
            <TicketIcon size={36} />
            <span className="eyebrow">YOUR SEAT IS WAITING</span>
            <h1>Join the journey.</h1>
            <p>
              You’re invited to table <strong>{code}</strong>.<br />
              Sign in to take your place.
            </p>
            <button
              className="primary large"
              onClick={login}
              disabled={!session?.googleEnabled}
            >
              <GoogleMark />
              Continue with Google
            </button>
            {session?.devLoginEnabled && (
              <DevLogin
                onLogin={(u) => setSession((s) => (s ? { ...s, user: u } : s))}
                onError={setError}
              />
            )}
            <button className="text-button" onClick={() => navigate(null)}>
              <ArrowLeft size={16} /> Back to the station
            </button>
          </div>
        </main>
      ) : !room ? (
        <main className="center-page">
          <div className="boarding-pass">
            <Users size={32} />
            <h1>{needsJoin ? "Take your seat." : "Finding your table…"}</h1>
            <p>Table {code}</p>
            {needsJoin ? (
              <button
                className="primary large"
                onClick={() => join(code)}
                disabled={busy}
              >
                {busy ? "Joining…" : "Join this table"}
                <ArrowRight size={17} />
              </button>
            ) : (
              <RefreshCw className="loading" />
            )}
            <button className="text-button" onClick={() => navigate(null)}>
              <ArrowLeft size={16} /> Back to the station
            </button>
          </div>
        </main>
      ) : (
        <main className={`table-page ${room.game ? "in-game" : ""}`}>
          <div className="table-heading">
            <div>
              <button className="text-button" onClick={() => navigate(null)}>
                <ArrowLeft size={15} /> Station
              </button>
              <span className="table-code">TABLE {room.id}</span>
              <span className={`connection ${connected ? "" : "offline"}`}>
                <i />
                {connected ? "All aboard" : "Reconnecting…"}
              </span>
            </div>
            <button className="secondary compact" onClick={copy}>
              {copied ? <Check size={15} /> : <Copy size={15} />}{" "}
              {copied ? "Link copied" : "Invite friends"}
            </button>
          </div>
          {!room.game ? (
            <section className="lobby">
              <div className="lobby-main">
                <span className="eyebrow">PRIVATE DEPARTURE LOUNGE</span>
                <h1>
                  Good company.
                  <br />
                  <em>Great journeys.</em>
                </h1>
                <p>
                  Share your table link. When everyone is here,
                  <br />
                  the adventure is yours to begin.
                </p>
                <div className="invite-link">
                  <span>
                    {location.host}/room/{room.id}
                  </span>
                  <button aria-label="Copy invitation link" onClick={copy}>
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
                <div className="lobby-roster">
                  {Array.from({ length: 5 }, (_, i) => {
                    const member = room.members[i];
                    return (
                      <div
                        className={`lobby-seat ${member ? "occupied" : ""}`}
                        key={i}
                      >
                        {member ? (
                          <>
                            <span className="seat-avatar">
                              {member.name.slice(0, 1).toUpperCase()}
                            </span>
                            <span>
                              {member.name}
                              {member.id === user.id && <small>YOU</small>}
                            </span>
                            {member.id === room.hostId ? (
                              <Crown size={17} />
                            ) : (
                              <Check size={17} />
                            )}
                          </>
                        ) : (
                          <>
                            <span className="seat-avatar">
                              <Plus size={19} />
                            </span>
                            <span>An open seat</span>
                            <small>{i < 2 ? "REQUIRED" : "OPTIONAL"}</small>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                {room.hostId === user.id ? (
                  <button
                    className="primary large"
                    disabled={busy || room.members.length < 2}
                    onClick={() =>
                      run(async () =>
                        acceptRoom(
                          await api<RoomView>(`/api/rooms/${room.id}/start`, {
                            revision: room.revision,
                          }),
                        ),
                      )
                    }
                  >
                    {room.members.length < 2
                      ? "Waiting for a friend"
                      : "Let’s play"}
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <div className="waiting-note">
                    Your host will start when everyone is ready.
                  </div>
                )}
                <button
                  className="text-button leave-table"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await api(`/api/rooms/${room.id}/leave`, {
                        revision: room.revision,
                      });
                      navigate(null);
                    })
                  }
                >
                  Leave this table
                </button>
              </div>
              <div className="lobby-map">
                <Board decorative />
                <div className="lobby-map-caption">
                  <TrainFront size={26} />
                  <div>
                    <strong>Next stop: everywhere.</strong>
                    <span>47 cities · One grand adventure</span>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <GameTable
              key={room.id}
              game={room.game}
              busy={busy || !connected}
              send={action}
              onRules={() => setRules(true)}
              onNew={create}
            />
          )}
        </main>
      )}
      {!room?.game && (
        <footer className="site-footer">
          <span>
            <span className="footer-star">✳</span> A LITTLE EXPERIMENT. A GRAND
            ADVENTURE.
          </span>
          <span>
            Unofficial fan project · Ticket to Ride is a trademark of Days of
            Wonder.
          </span>
        </footer>
      )}
      {rules && <Rules onClose={() => setRules(false)} />}
    </>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M19 10.2c0-.6-.1-1.2-.2-1.8H10v3.4h5a4.3 4.3 0 0 1-1.8 2.8v2.3h3A9 9 0 0 0 19 10.2ZM10 19c2.4 0 4.5-.8 6-2.1l-3-2.3c-.8.5-1.8.8-3 .8a5.5 5.5 0 0 1-5.2-3.8H1.7v2.4A9 9 0 0 0 10 19ZM4.8 11.6a5.4 5.4 0 0 1 0-3.2V6H1.7a9 9 0 0 0 0 8l3.1-2.4ZM10 4.6c1.3 0 2.4.4 3.3 1.3l2.5-2.5A8.8 8.8 0 0 0 10 1a9 9 0 0 0-8.3 5l3.1 2.4A5.5 5.5 0 0 1 10 4.6Z"
      />
    </svg>
  );
}
function DevLogin({
  onLogin,
  onError,
}: {
  onLogin: (user: User) => void;
  onError: (e: string) => void;
}) {
  const [name, setName] = useState("");
  return (
    <form
      className="dev-login"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          const r = await api<{ user: User }>("/api/auth/dev", { name });
          onLogin(r.user);
        } catch (e) {
          onError(e instanceof Error ? e.message : "Could not sign in.");
        }
      }}
    >
      <label>
        LOCAL TEST PLAYER
        <input
          placeholder="Player name"
          required
          minLength={1}
          maxLength={40}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <button className="secondary" type="submit">
        Enter
      </button>
    </form>
  );
}

function GameTable({
  game,
  busy,
  send,
  onRules,
  onNew,
}: {
  game: GameView;
  busy: boolean;
  send: (a: GameAction) => Promise<boolean | undefined>;
  onRules: () => void;
  onNew: () => void;
}) {
  const [route, setRoute] = useState<Route | null>(null),
    [station, setStation] = useState<string | null>(null),
    [focusTicket, setFocusTicket] = useState<Ticket | null>(null),
    [showResults, setShowResults] = useState(game.status === "finished");
  const self = game.self!,
    me = game.players.find((p) => p.id === self?.id),
    current = game.players.find((p) => p.id === game.currentPlayerId);
  const myTurn = game.status === "playing" && game.currentPlayerId === self?.id;
  const turnFree =
    myTurn && !game.tunnel && !self?.ticketOffer && game.drawnTrainCards === 0;
  useEffect(() => {
    if (game.status === "finished") setShowResults(true);
  }, [game.status]);
  if (!self || !me) return <p>Reconnect to return to your seat.</p>;
  const chooseRoute = (r: Route) => {
    setRoute(r);
    setStation(null);
  };
  const banner =
    game.status === "finished"
      ? "The journey is complete."
      : game.status === "setup"
        ? self.ticketOffer
          ? "Choose your destinations."
          : "Waiting for everyone to choose tickets."
        : myTurn
          ? game.tunnel
            ? "A tunnel lies ahead."
            : self.ticketOffer
              ? "Choose your destination tickets."
              : game.drawnTrainCards
                ? "Pick your second train card."
                : "Your turn. Where to next?"
          : `${current?.name} is planning the next move.`;
  return (
    <>
      <div className="player-strip">
        {game.players.map((p) => (
          <div
            key={p.id}
            className={`player-chip ${p.id === game.currentPlayerId && game.status === "playing" ? "current" : ""}`}
            style={{ "--player-color": p.color } as React.CSSProperties}
          >
            <span className="player-token">
              {p.name.slice(0, 1).toUpperCase()}
            </span>
            <div className="player-identity">
              <strong>
                {p.name}
                {p.id === self.id && <small>YOU</small>}
              </strong>
              <span>
                <TrainFront size={12} />
                {p.trains}
                <TicketIcon size={12} />
                {p.ticketCount}
                <span className="hand-icon">▤</span>
                {p.trainCardCount}
                <Home size={12} />
                {p.stationsRemaining}
              </span>
            </div>
            <span className="player-score">
              {p.score}
              <small>PTS</small>
            </span>
          </div>
        ))}
      </div>
      <div className={`turn-banner ${myTurn ? "your-turn" : ""}`} role="status">
        <span>
          <span className="turn-dot" />
          {banner}
        </span>
        <span>
          {game.finalTurnsRemaining !== null ? (
            <>
              <Flag size={15} /> FINAL ROUND · {game.finalTurnsRemaining} TURNS
              LEFT
            </>
          ) : game.status === "setup" ? (
            "PREPARING FOR DEPARTURE"
          ) : (
            `TURN ${game.turn}`
          )}
        </span>
      </div>
      <div className="game-layout">
        <div className="game-main">
          <Board
            game={game}
            selected={route?.id}
            focusedCities={
              focusTicket ? [focusTicket.from, focusTicket.to] : []
            }
            onRoute={chooseRoute}
            onCity={(id) => {
              setStation(id);
              setRoute(null);
            }}
          />
          <section className="hand-panel">
            <div className="panel-heading">
              <h2>Your train cards</h2>
              <span>
                <LockKeyhole size={12} /> ONLY YOU CAN SEE THESE
              </span>
            </div>
            <div className="hand-cards">
              {allColors.map((color) => (
                <TrainCard
                  key={color}
                  color={color}
                  count={self.hand[color]}
                  small
                />
              ))}
            </div>
          </section>
        </div>
        <aside className="game-sidebar">
          <section className="market-panel">
            <div className="panel-heading">
              <h2>The carriage market</h2>
              <span>DRAW CARDS</span>
            </div>
            <div className="market-cards">
              {game.market.map((color, i) => (
                <TrainCard
                  key={i}
                  color={color}
                  onClick={() => void send({ type: "drawTrain", source: i })}
                  disabled={
                    busy ||
                    !myTurn ||
                    !!game.tunnel ||
                    !!self.ticketOffer ||
                    (game.drawnTrainCards > 0 && color === "locomotive")
                  }
                />
              ))}
              <TrainCard
                color="back"
                onClick={() => void send({ type: "drawTrain", source: "deck" })}
                disabled={
                  busy ||
                  !myTurn ||
                  !!game.tunnel ||
                  !!self.ticketOffer ||
                  game.trainDeckCount + game.trainDiscardCount === 0
                }
              />
            </div>
            <p className="panel-hint">
              Take two cards. A face-up locomotive counts as both.
            </p>
            <div className="deck-count">
              <span>{game.trainDeckCount} in the deck</span>
              <span>{game.trainDiscardCount} discarded</span>
            </div>
          </section>
          <section className="tickets-panel">
            <div className="panel-heading">
              <h2>Your destinations</h2>
              <span>
                {self.tickets.filter((t) => t.completed).length}/
                {self.tickets.length}
              </span>
            </div>
            <div className="ticket-list">
              {self.tickets.map((ticket) => (
                <Destination
                  key={ticket.id}
                  ticket={ticket}
                  completed={ticket.completed}
                  selected={focusTicket?.id === ticket.id}
                  onClick={() =>
                    setFocusTicket((t) => (t?.id === ticket.id ? null : ticket))
                  }
                />
              ))}
            </div>
            <button
              className="secondary full-width"
              onClick={() => void send({ type: "drawTickets" })}
              disabled={busy || !turnFree || game.ticketDeckCount === 0}
            >
              <TicketIcon size={16} />
              Draw destination tickets <Plus size={15} />
            </button>
            <p className="panel-hint">
              Draw up to 3, keep at least 1. {game.ticketDeckCount} remaining.
            </p>
          </section>
          <section className="journal-panel">
            <div className="panel-heading">
              <h2>Travel journal</h2>
              <span>LIVE</span>
            </div>
            <ol>
              {game.log
                .slice(-5)
                .reverse()
                .map((log, i) => (
                  <li key={`${log.turn}-${i}`}>
                    <span>{String(log.turn).padStart(2, "0")}</span>
                    <p>{log.message}</p>
                  </li>
                ))}
            </ol>
          </section>
          <div className="sidebar-footer">
            <button className="text-button" onClick={onRules}>
              <BookOpen size={15} /> Rulebook
            </button>
            {game.status === "finished" ? (
              <button
                className="text-button"
                onClick={() => setShowResults(true)}
              >
                <Trophy size={15} /> Results
              </button>
            ) : (
              <button
                className="text-button"
                disabled={busy || !turnFree}
                title="Only possible when no cards, tickets, routes, or stations can be played"
                onClick={() => void send({ type: "pass" })}
              >
                No legal moves?
              </button>
            )}
          </div>
        </aside>
      </div>
      {(route || station) && (
        <PaymentModal
          key={route?.id ?? station!}
          route={route}
          station={station}
          game={game}
          enabled={turnFree && !busy}
          onClose={() => {
            setRoute(null);
            setStation(null);
          }}
          send={send}
        />
      )}
      {self.ticketOffer && (
        <TicketChoice
          key={self.ticketOffer.tickets.map((t) => t.id).join(",")}
          offer={self.ticketOffer}
          busy={busy}
          send={send}
        />
      )}
      {game.tunnel?.playerId === self.id && (
        <TunnelModal game={game} busy={busy} send={send} />
      )}
      {showResults && game.results && (
        <Modal
          title="What a journey."
          onClose={() => setShowResults(false)}
          wide
        >
          <div className="results-lead">
            <Trophy size={36} />
            <h3>
              {game.winnerIds
                .map((id) => game.players.find((p) => p.id === id)?.name)
                .join(" & ")}{" "}
              {game.winnerIds.length > 1
                ? "share the victory."
                : "takes the victory."}
            </h3>
          </div>
          <div className="results-table-wrap">
            <table className="results-table">
              <thead>
                <tr>
                  <th>CONDUCTOR</th>
                  <th>ROUTES</th>
                  <th>TICKETS</th>
                  <th>STATIONS</th>
                  <th>LONGEST</th>
                  <th>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {[...game.results]
                  .sort((a, b) => b.total - a.total)
                  .map((r) => (
                    <tr key={r.playerId}>
                      <th>
                        {game.players.find((p) => p.id === r.playerId)?.name}
                        {game.winnerIds.includes(r.playerId) && " ♛"}
                      </th>
                      <td>{r.routePoints}</td>
                      <td>
                        {r.ticketPoints > 0 ? "+" : ""}
                        {r.ticketPoints}
                      </td>
                      <td>+{r.stationPoints}</td>
                      <td>
                        {r.longestPath} trains <small>+{r.longestBonus}</small>
                      </td>
                      <td>
                        <strong>{r.total}</strong>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {game.results.map((r) => (
            <details className="ticket-breakdown" key={r.playerId}>
              <summary>
                {game.players.find((p) => p.id === r.playerId)?.name} ·{" "}
                {r.completedTickets} completed tickets
              </summary>
              {r.tickets.map((t) => (
                <p key={t.id}>
                  <span>
                    {cityName(t.from)} → {cityName(t.to)}
                  </span>
                  <strong>
                    {t.completed ? "+" : "−"}
                    {t.points}
                  </strong>
                </p>
              ))}
            </details>
          ))}
          <button className="primary large" onClick={onNew}>
            Another adventure
            <ArrowRight size={18} />
          </button>
        </Modal>
      )}
    </>
  );
}

function PaymentModal({
  route,
  station,
  game,
  enabled,
  onClose,
  send,
}: {
  route: Route | null;
  station: string | null;
  game: GameView;
  enabled: boolean;
  onClose: () => void;
  send: (a: GameAction) => Promise<boolean | undefined>;
}) {
  const hand = game.self!.hand,
    me = game.players.find((p) => p.id === game.self!.id)!;
  const cost = route?.length ?? 4 - me.stationsRemaining;
  const colors: CardColor[] =
    route && route.color !== "gray" ? [route.color, "locomotive"] : allColors;
  const affordable = colors.filter((c) =>
    c === "locomotive"
      ? hand.locomotive >= cost
      : hand.locomotive >= Math.max(route?.locomotives ?? 0, cost - hand[c]),
  );
  const defaultColor = (affordable.length ? affordable : colors).reduce(
    (a, b) => (hand[a] >= hand[b] ? a : b),
  );
  const [color, setColor] = useState<CardColor>(defaultColor),
    [wilds, setWilds] = useState(
      Math.max(route?.locomotives ?? 0, cost - hand[defaultColor]),
    );
  const owner = route
    ? game.players.find((p) => p.id === game.claimedRoutes[route.id])
    : game.players.find((p) => p.id === game.stations[station!]);
  const parallelOwner = route?.parallel
    ? ROUTES.filter((r) => r.parallel === route.parallel && r.id !== route.id)
        .map((r) => game.claimedRoutes[r.id])
        .find(Boolean)
    : null;
  const blockedParallel =
    !!parallelOwner && (game.players.length < 4 || parallelOwner === me.id);
  const locomotiveCount =
    color === "locomotive" ? cost : Math.max(route?.locomotives ?? 0, wilds);
  const enough =
    locomotiveCount <= cost &&
    hand.locomotive >= locomotiveCount &&
    (color === "locomotive" || hand[color] >= cost - locomotiveCount);
  const legal =
    enabled &&
    !owner &&
    !blockedParallel &&
    enough &&
    (route ? me.trains >= cost : me.stationsRemaining > 0);
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      title={
        route
          ? `${cityName(route.from)} → ${cityName(route.to)}`
          : `Station at ${cityName(station!)}`
      }
      onClose={onClose}
    >
      <div className="route-facts">
        <span>
          <TrainFront size={17} />
          {cost} {route ? "trains" : "cards"}
        </span>
        {route && <span>{ROUTE_POINTS[cost]} points</span>}
        {route?.tunnel && (
          <span>
            <Mountain size={16} />
            Tunnel
          </span>
        )}
        {!!route?.locomotives && (
          <span>
            <Waves size={16} />
            {route.locomotives} wild required
          </span>
        )}
      </div>
      {owner ? (
        <p className="modal-intro">
          {owner.name} already owns this {route ? "route" : "station"}.
        </p>
      ) : blockedParallel ? (
        <p className="modal-intro">
          {parallelOwner === me.id
            ? "You cannot own both tracks of a double route."
            : "With 2–3 players, only one track of a double route can be claimed."}
        </p>
      ) : (
        <>
          <p className="modal-intro">
            {route?.tunnel
              ? "Choose your payment. Three revealed cards may increase the tunnel cost."
              : route
                ? "Choose the train cards you’ll spend to claim this route."
                : "Use one connected opponent route for destination tickets. Each unbuilt station is worth 4 points at the end."}
          </p>
          <label className="field-label">
            PAYMENT COLOR
            <select
              value={color}
              onChange={(e) => {
                const c = e.target.value as CardColor;
                setColor(c);
                setWilds(Math.max(route?.locomotives ?? 0, cost - hand[c]));
              }}
            >
              {colors.map((c) => (
                <option key={c} value={c}>
                  {c === "locomotive"
                    ? "All locomotives"
                    : c[0].toUpperCase() + c.slice(1)}{" "}
                  · {hand[c]} available
                </option>
              ))}
            </select>
          </label>
          {color !== "locomotive" && (
            <label className="field-label">
              LOCOMOTIVES TO USE
              <select
                value={Math.min(cost, locomotiveCount)}
                onChange={(e) => setWilds(Number(e.target.value))}
              >
                {Array.from(
                  { length: cost - (route?.locomotives ?? 0) + 1 },
                  (_, i) => i + (route?.locomotives ?? 0),
                ).map((n) => (
                  <option key={n} value={n}>
                    {n} locomotive{n === 1 ? "" : "s"} + {cost - n} {color}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="payment-summary">
            <span>You’ll spend</span>
            <strong>
              {color === "locomotive"
                ? `${cost} locomotives`
                : `${cost - locomotiveCount} ${color}${locomotiveCount ? ` + ${locomotiveCount} locomotive${locomotiveCount > 1 ? "s" : ""}` : ""}`}
            </strong>
          </div>
          {!enough && (
            <p className="inline-warning">
              You don’t have enough cards for this payment yet.
            </p>
          )}
          {!enabled && (
            <p className="panel-hint">
              You can do this at the start of your turn.
            </p>
          )}
          <button
            className="primary full-width"
            disabled={!legal || submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                const ok = await send(
                  route
                    ? {
                        type: "claimRoute",
                        routeId: route.id,
                        color,
                        locomotives: locomotiveCount,
                      }
                    : {
                        type: "buildStation",
                        cityId: station!,
                        color,
                        locomotives: locomotiveCount,
                      },
                );
                if (ok) onClose();
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {route?.tunnel
              ? "Attempt tunnel"
              : route
                ? "Claim route"
                : "Build station"}
            <ArrowRight size={17} />
          </button>
        </>
      )}
    </Modal>
  );
}

function TicketChoice({
  offer,
  busy,
  send,
}: {
  offer: NonNullable<NonNullable<GameView["self"]>["ticketOffer"]>;
  busy: boolean;
  send: (a: GameAction) => Promise<boolean | undefined>;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  return (
    <Modal
      title={offer.initial ? "Where will you go?" : "A new destination awaits."}
      wide
    >
      <span className="eyebrow">
        {offer.initial ? "YOUR JOURNEY STARTS HERE" : "DESTINATION TICKETS"}
      </span>
      <p className="modal-intro">
        Keep at least {offer.minKeep} tickets. Connect the two cities to earn
        their points; unfinished tickets subtract points at the end.
      </p>
      <div className="ticket-choices">
        {offer.tickets.map((t) => (
          <Destination
            key={t.id}
            ticket={t}
            selected={selected.includes(t.id)}
            onClick={() =>
              setSelected((s) =>
                s.includes(t.id) ? s.filter((id) => id !== t.id) : [...s, t.id],
              )
            }
          />
        ))}
      </div>
      <div className="ticket-choice-footer">
        <span>
          {selected.length} selected · keep at least {offer.minKeep}
        </span>
        <button
          className="primary"
          disabled={busy || selected.length < offer.minKeep}
          onClick={() =>
            void send({ type: "chooseTickets", ticketIds: selected })
          }
        >
          Keep {selected.length} tickets <Check size={17} />
        </button>
      </div>
    </Modal>
  );
}

function TunnelModal({
  game,
  busy,
  send,
}: {
  game: GameView;
  busy: boolean;
  send: (a: GameAction) => Promise<boolean | undefined>;
}) {
  const tunnel = game.tunnel!,
    hand = game.self!.hand;
  const remaining = { ...hand };
  const [wilds, setWilds] = useState(
    tunnel.color === "locomotive"
      ? tunnel.extraCost
      : Math.max(0, tunnel.extraCost - remaining[tunnel.color]),
  );
  const count = tunnel.color === "locomotive" ? tunnel.extraCost : wilds;
  const enough =
    remaining.locomotive >= count &&
    (tunnel.color === "locomotive" ||
      remaining[tunnel.color] >= tunnel.extraCost - count);
  return (
    <Modal title="Into the tunnel.">
      <p className="modal-intro">
        The tunnel revealed these cards.{" "}
        {tunnel.extraCost
          ? `Pay ${tunnel.extraCost} additional matching card${tunnel.extraCost === 1 ? "" : "s"} or withdraw. Withdrawing returns your original cards and ends your turn.`
          : "No extra payment is needed."}
      </p>
      <div className="tunnel-reveal">
        {tunnel.revealed.map((color, i) => (
          <TrainCard key={i} color={color} />
        ))}
      </div>
      {tunnel.color !== "locomotive" && tunnel.extraCost > 0 && (
        <label className="field-label">
          EXTRA PAYMENT
          <select
            value={wilds}
            onChange={(e) => setWilds(Number(e.target.value))}
          >
            {Array.from({ length: tunnel.extraCost + 1 }, (_, n) => (
              <option key={n} value={n}>
                {tunnel.extraCost - n} {tunnel.color} + {n} locomotives
              </option>
            ))}
          </select>
        </label>
      )}
      {!enough && (
        <p className="inline-warning">
          Not enough cards to complete this tunnel.
        </p>
      )}
      <div className="modal-actions">
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void send({ type: "resolveTunnel", pay: false })}
        >
          Withdraw
        </button>
        <button
          className="primary"
          disabled={busy || !enough}
          onClick={() =>
            void send({ type: "resolveTunnel", pay: true, locomotives: count })
          }
        >
          {tunnel.extraCost ? "Pay & claim" : "Claim tunnel"}
          <ArrowRight size={16} />
        </button>
      </div>
    </Modal>
  );
}
